import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui-custom/Dialog";
import Button from "../ui-custom/Button";
import { Input, Textarea, Label } from "../ui-custom/Input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui-custom/Select";
import { Users } from "lucide-react";
import { fetchOrganizationMembers } from "api/design/service";

const sourceTypes = [
  { label: "Wallet", value: "WALLET" },
  { label: "Personal", value: "PERSONAL" },
];

function getMemberDisplayName(m) {
  const fn = m?.user?.first_name || "";
  const ln = m?.user?.last_name || "";
  const full = `${fn} ${ln}`.trim();
  return full || m?.user?.username || m?.user?.email || `Member ${m?.id}`;
}

function getMemberProfession(m) {
  return (
    m?.employee_profession ||
    m?.profession ||
    m?.user?.profession ||
    m?.role ||
    m?.designation ||
    ""
  );
}

function isOwnerMember(m) {
  const roleText = [
    m?.role,
    m?.designation,
    m?.employee_profession,
    m?.profession,
    m?.user?.role,
    m?.user?.designation,
    m?.user?.profession,
  ]
    .filter(Boolean)
    .join(" ");

  return /\bowner\b/i.test(roleText);
}

export function AllocateFundsDialog({ open, onOpenChange, onSubmit }) {
  const [employeeId, setEmployeeId] = useState("");
  const [amount, setAmount] = useState("");
  const [spendableAmount, setSpendableAmount] = useState("");
  const [spendableError, setSpendableError] = useState("");
  const [sourceType, setSourceType] = useState("WALLET");
  const [remarks, setRemarks] = useState("");

  const amountNum = parseFloat(amount) || 0;
  const spendableNum =
    spendableAmount !== "" && !Number.isNaN(parseFloat(spendableAmount))
      ? parseFloat(spendableAmount)
      : amountNum;

  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const orgId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("organization") || "{}")?.id;
    } catch (_) {
      return null;
    }
  }, []);

  const memberOptions = useMemo(() => {
    return (members || [])
      .filter((m) => !isOwnerMember(m))
      .map((m) => {
        const id = String(m?.id ?? "");
        return {
          id,
          name: getMemberDisplayName(m),
          profession: getMemberProfession(m),
        };
      })
      .filter((o) => o.id);
  }, [members]);

  const selectedMember = memberOptions.find((m) => m.id === employeeId);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!open) return;
      if (!orgId) return;
      setLoadingMembers(true);
      try {
        const list = await fetchOrganizationMembers(orgId);
        if (!cancelled) setMembers(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("Failed to load organization members", e);
        if (!cancelled) setMembers([]);
      } finally {
        if (!cancelled) setLoadingMembers(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [open, orgId]);

  const handleSubmit = () => {
    if (!employeeId || !amount) return;
  };

  const handleAmountChange = (e) => {
    const next = e.target.value;
    setAmount(next);

    const nextAmountNum =
      next !== "" && !Number.isNaN(parseFloat(next)) ? parseFloat(next) : null;
    const spendableParsed =
      spendableAmount !== "" && !Number.isNaN(parseFloat(spendableAmount))
        ? parseFloat(spendableAmount)
        : null;

    // If user lowers allocated amount below spendable amount, clamp spendable.
    if (
      nextAmountNum !== null &&
      spendableParsed !== null &&
      spendableParsed > nextAmountNum
    ) {
      setSpendableAmount(String(nextAmountNum));
      setSpendableError("");
    }
  };

  const handleSpendableChange = (e) => {
    const next = e.target.value;

    if (next === "") {
      setSpendableAmount("");
      setSpendableError("");
      return;
    }

    const nextNum = parseFloat(next);
    if (Number.isNaN(nextNum)) {
      setSpendableAmount(next);
      setSpendableError("");
      return;
    }

    const allocNum =
      amount !== "" && !Number.isNaN(parseFloat(amount)) ? parseFloat(amount) : null;

    if (allocNum !== null && nextNum > allocNum) {
      // Prevent entering spendable amount above allocated amount.
      setSpendableAmount(String(allocNum));
      setSpendableError("Spendable amount cannot exceed allocated amount.");
      return;
    }

    setSpendableAmount(next);
    setSpendableError("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-sm-secondary/10 rounded-lg">
              <Users className="w-5 h-5 text-sm-secondary" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold">Allocate Funds</DialogTitle>
              <p className="text-sm text-text-gray">
                Assign petty cash to a team member for their site expenses.
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 p-6 py-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Select Employee</Label>
            <Select
              value={employeeId}
              onValueChange={setEmployeeId}
              placeholder="Choose team member"
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {memberOptions.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name}{m.profession ? ` (${m.profession})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {loadingMembers && (
              <p className="text-xs text-text-gray">Loading...</p>
            )}
            {selectedMember && selectedMember.profession ? (
              <p className="text-xs text-text-gray">{selectedMember.profession}</p>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Amount (₹)</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={handleAmountChange}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Spendable Amount (₹)</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={spendableAmount}
                onChange={handleSpendableChange}
                max={amountNum || undefined}
                className={spendableError ? "border-red-500 focus:border-red-500" : ""}
              />
              {spendableError ? (
                <p className="text-xs text-red-600">{spendableError}</p>
              ) : null}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Source Type</Label>
            <Select value={sourceType} onValueChange={setSourceType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sourceTypes.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Remarks (optional)</Label>
            <Textarea
              placeholder="Purpose of allocation..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!employeeId || !amount) return;
              if (!orgId) return;
              setSubmitting(true);
              try {
                await onSubmit?.({
                  employeeId: Number(employeeId),
                  amount: amountNum,
                  spendableAmount: spendableNum,
                  sourceType,
                  remarks: remarks || "",
                });
                setEmployeeId("");
                setAmount("");
                setSpendableAmount("");
                setSpendableError("");
                setSourceType("WALLET");
                setRemarks("");
                onOpenChange(false);
              } catch (e) {
                console.error("Allocation failed", e);
                alert(e?.message || "Failed to allocate funds");
              } finally {
                setSubmitting(false);
              }
            }}
            disabled={!employeeId || !amount || submitting}
            variant="accent"
          >
            {submitting ? "Allocating..." : "Allocate Funds"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
